/**
 * Saved filters in the rail: pick a named preset for this experience, save
 * the current filters under a name, delete the selected preset.
 */
import { useState } from 'react'
import { IconDeviceFloppy, IconTrash } from '@tabler/icons-react'
import type { SavedFilterPreset } from '../../api/types'
import { Button, PickerRow, Popover, TextInput } from '../../ui/kit'
import { SAVED_FILTER_LIMIT, SAVED_FILTER_NAME_LIMIT } from './presets'

export function SavedFilters({ presets, selectedId, saving, ready, error, onLoad, onSave, onDelete }: {
  presets: SavedFilterPreset[]; selectedId: string | null; saving: boolean; ready: boolean; error: string | null
  onLoad: (preset: SavedFilterPreset) => void
  onSave: (name: string) => Promise<void>
  onDelete: (id: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [saveError, setSaveError] = useState<string | null>(null)
  const full = presets.length >= SAVED_FILTER_LIMIT
  const selected = presets.find(p => p.id === selectedId) ?? null
  return (
    <section className="rail-sec">
      <div className="rail-sh"><span className="t-section">Saved filters</span><span className="hint">{presets.length ? `${presets.length} of ${SAVED_FILTER_LIMIT}` : 'none yet'}</span></div>
      <PickerRow label="Saved" options={presets.map(p => ({ value: p.id, label: p.name }))} value={selectedId} searchable={presets.length > 8} emptyLabel="None" clearable={false}
        summaryOverride={!ready ? 'Loading…' : selected ? selected.name : presets.length ? 'Choose a filter' : 'No saved filters'} busy={saving}
        onChange={id => { const preset = presets.find(p => p.id === id); if (preset) onLoad(preset) }} />
      <div className="rail-actions">
        <Popover open={open} onClose={() => setOpen(false)} align="start" width={280} anchor={
          <button type="button" className="btn is-small" disabled={!ready || saving} onClick={() => { setSaveError(null); setOpen(o => !o) }} aria-haspopup="dialog" aria-expanded={open}><IconDeviceFloppy className="ic-sm" />Save current…</button>
        }>
          <div className="pop-title">Save the current filters</div>
          <TextInput value={name} onChange={setName} placeholder="Filter name" maxLength={SAVED_FILTER_NAME_LIMIT} autoFocus ariaLabel="Filter name"
            onKeyDown={e => { if (e.key === 'Enter' && name.trim()) { void onSave(name).then(() => { setOpen(false); setName('') }).catch(f => setSaveError(f instanceof Error ? f.message : 'The filter was not saved.')) } }} />
          <div className="form-actions">
            <span className="sp" />
            <Button small onClick={() => setOpen(false)}>Cancel</Button>
            <Button kind="primary" small disabled={!name.trim() || saving || full} busy={saving} onClick={() => { void onSave(name).then(() => { setOpen(false); setName('') }).catch(f => setSaveError(f instanceof Error ? f.message : 'The filter was not saved.')) }}>Save</Button>
          </div>
          {full ? <div className="pop-note">You can save up to 50 filters. Delete one to save another.</div> : null}
          {saveError ? <div className="fld-error">{saveError}</div> : null}
        </Popover>
        {selected ? <button type="button" className="btn is-small is-danger" disabled={saving} onClick={() => onDelete(selected.id)} aria-label={`Delete saved filter ${selected.name}`}><IconTrash className="ic-sm" />Delete</button> : null}
      </div>
      {error ? <div className="fld-error">{error}</div> : null}
    </section>
  )
}
